import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Head from "../components/Head";
import auth from '../utils/auth';
import '../styles/ResetPasswordPage.css';
import { scrollToTop } from '../hooks/useScrollToTop';

const ResetPasswordPage = () => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { token } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    const validateToken = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/validate-reset-token/${token}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );

        if (response.status !== 200) {
          navigate('/404');
        }
      } catch (err) {
        navigate('/404');
      } finally {
        setIsLoading(false);
      }
    };

    validateToken();
  }, [token, navigate]);

  const toggleNewPasswordVisibility = () => {
    setShowNewPassword(!showNewPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (!newPassword || !confirmPassword) {
      setError('Båda fälten är obligatoriska.');
      return;
    }

    if (newPassword.length < 6) {
      setError('Lösenordet måste vara minst 6 tecken långt.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Lösenorden matchar inte.');
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/reset-password/${token}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ newPassword }),
        }
      );

      const data = await response.json();

      if (response.status !== 200) {
        setError(data.message || 'Ett fel inträffade. Försök igen.');
        return;
      }

      if (data.token && data.user) {
        setMessage('Lösenordet har återställts och du loggas in automatiskt. Omdirigeras...');

        setTimeout(() => {
          auth.login(data.token, data.user);
          navigate('/');
          scrollToTop();
        }, 3000);
      } else {
        setMessage(data.message);
      }
    } catch (err) {
      setError('Ett serverfel inträffade. Försök igen senare.');
    }
  };

  if (isLoading) {
    return (
      <div className="reset-password-page">
        <Head title="EssayWriter | Återställ lösenord" />
        <div className="reset-password-container">
          <h2 className="reset-password-title">Återställ lösenord</h2>
          <div className="reset-password-loading">Verifierar återställningslänk...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="reset-password-page">
      <Head title="EssayWriter | Återställ lösenord" />
      <div className="reset-password-container">
        <h2 className="reset-password-title">Återställ lösenord</h2>
        <div className="reset-password-form">
          {error && <p className="reset-password-error">{error}</p>}
          {message && <p className="reset-password-success">{message}</p>}
          <form onSubmit={handleSubmit}>
            <div className="reset-password-form-group">
              <label htmlFor="newPassword" className="reset-password-label">Nytt lösenord:</label>
              <div className="reset-password-password-input-container">
                <input
                  type={showNewPassword ? "text" : "password"}
                  id="newPassword"
                  className="reset-password-input"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength="6"
                />
                <button
                  type="button"
                  className="reset-password-password-toggle-btn"
                  onClick={toggleNewPasswordVisibility}
                >
                  {showNewPassword ? "Dölj" : "Visa"}
                </button>
              </div>
            </div>
            <div className="reset-password-form-group">
              <label htmlFor="confirmPassword" className="reset-password-label">Bekräfta lösenord:</label>
              <div className="reset-password-password-input-container">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  id="confirmPassword"
                  className="reset-password-input"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength="6"
                />
                <button
                  type="button"
                  className="reset-password-password-toggle-btn"
                  onClick={toggleConfirmPasswordVisibility}
                >
                  {showConfirmPassword ? "Dölj" : "Visa"}
                </button>
              </div>
            </div>
            <button type="submit" className="reset-password-button">Återställ lösenord</button>
          </form>
        </div>
      </div>
      <p className="reset-password-login-link">
        <a href="/logga-in">Tillbaka till inloggning</a>
      </p>
    </div>
  );
};

export default ResetPasswordPage;